import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from './ui/Button';
import ReportIssueButton from './ReportIssueButton';
import { handleChunkLoadError } from '../../utils/chunkErrorHandler';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, resetKey: props.resetKey };
  }

  // Reset error state when the route changes (resetKey = location.pathname).
  // Without this, the ErrorBoundary stays in its error state after the user
  // navigates away from the page that threw — the error UI persists even
  // though the new route would render fine. By keying the reset on
  // pathname, navigation to any other route clears the error and gives the
  // new page a fresh render.
  static getDerivedStateFromProps(props, state) {
    if (state.hasError && props.resetKey !== state.resetKey) {
      return { hasError: false, error: null, errorInfo: null, resetKey: props.resetKey };
    }
    if (props.resetKey !== state.resetKey) {
      return { resetKey: props.resetKey };
    }
    return null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);

    // Auto-recover from chunk load failures (stale index.html after a
    // deployment). Instead of showing the error UI, reload the page so the
    // browser fetches a fresh index.html with the correct chunk hashes.
    // handleChunkLoadError() returns true (and triggers a reload) only for
    // ChunkLoadError / dynamic-import failures; all other errors fall
    // through to the normal error UI.
    if (handleChunkLoadError(error)) {
      return; // page reload is in progress — don't update state
    }

    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error
        ? `${this.state.error.message || String(this.state.error)}${this.state.errorInfo?.componentStack ? '\n' + this.state.errorInfo.componentStack : ''}`
        : 'Unknown error';
      return (
        <div
          style={{
            minHeight: '60vh',
            display: 'grid',
            placeItems: 'center',
            padding: '2rem 1rem',
          }}
        >
          <div
            style={{
              textAlign: 'center',
              maxWidth: '32rem',
              display: 'grid',
              gap: '1rem',
            }}
          >
            <span className="page-kicker">Something went wrong</span>
            <h1 className="page-title">Unexpected error</h1>
            <p className="page-subtitle">
              We hit an unexpected issue while loading this page. Try refreshing,
              or head back to explore more stories.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button variant="primary" onClick={this.handleReset}>
                Try Again
              </Button>
              <Link to="/">
                <Button variant="outline">Go Home</Button>
              </Link>
              <Link to="/library">
                <Button variant="ghost">Browse Library</Button>
              </Link>
            </div>
            <ReportIssueButton
              prefillCategory="BUG"
              prefillErrorMessage={errorMessage}
              prefillDescription="Error occurred while loading this page."
            />
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper that passes the current route's pathname as a `resetKey` to
 * ErrorBoundary. When the user navigates to a different route, the
 * ErrorBoundary clears its error state so the new page renders normally
 * instead of inheriting the stale error UI from the previous route.
 */
export const ErrorBoundaryWithRouteReset = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
};

export default ErrorBoundary;
